import { useGetLiveChatSchema } from '../../Chat/hooks'
import { LiveChatDataSchema } from '../../Chat/types'
import { clearLiveChatSessionData } from '../../Chat/utils'
import { LiveChatContent } from './LiveChatContent'
import { useQueryClient } from '@tanstack/react-query'
import { forwardRef, useCallback } from 'react'
import { MessageCircle } from 'lucide-react'

export const LiveChatComponent = forwardRef<HTMLDivElement, { id: string }>(({ id }, ref) => {
  const { data } = useGetLiveChatSchema({ contentId: id })
  const queryClient = useQueryClient()

  // Session data comes from the content response (backend checks Redis)
  const connectionData: LiveChatDataSchema | null =
    data?.dataSchema && 'success' in data.dataSchema && data.dataSchema.success
      ? (data.dataSchema as LiveChatDataSchema)
      : null

  const handleNewChat = useCallback(() => {
    // Clear local session data and invalidate query to refetch from backend
    clearLiveChatSessionData()
    queryClient.invalidateQueries({ queryKey: ['live-chat-schema', id] })
  }, [queryClient, id])

  // Show chat interface if we have valid connection data from Redis
  if (connectionData) {
    return (
      <LiveChatContent
        ref={ref}
        uiSchema={data.uiSchema}
        connectionData={connectionData}
        onNewChat={handleNewChat}
      />
    )
  }

  // No session in Redis - show message to use the form
  const errorMessage =
    data?.dataSchema && 'error' in data.dataSchema
      ? data.dataSchema.error
      : 'Use the form above to start a new chat session.'

  return (
    <div ref={ref} className="flex h-full flex-col items-center justify-center gap-6 p-8 text-center">
      <div className="rounded-full bg-muted p-4">
        <MessageCircle className="h-8 w-8 text-muted-foreground" />
      </div>
      <div className="space-y-2">
        <h3 className="text-lg font-medium">No Active Session</h3>
        <p className="max-w-sm text-sm text-muted-foreground">{errorMessage}</p>
      </div>
    </div>
  )
})

LiveChatComponent.displayName = 'LiveChatComponent'

export const LiveChatComponentLoading = forwardRef<HTMLDivElement>((_, ref) => {
  return <div ref={ref}>Loading...</div>
})

LiveChatComponentLoading.displayName = 'LiveChatComponentLoading'
